import Router from 'preact-router';
import { lazy, Suspense } from 'preact/compat';
import { useAuth } from './hooks/useAuth';
import { useSocket } from './hooks/useSocket';
import { JoinPage } from './pages/JoinPage';
import { ContributePage } from './pages/ContributePage';
import { ThreadPage } from './pages/ThreadPage';
import { CodeJoinPage } from './pages/CodeJoinPage';
import { HomePage } from './pages/HomePage';
import { NotFound } from './pages/NotFound';
import { Spinner } from './components/Spinner';
import { UpdateAvailableBanner } from './components/UpdateAvailableBanner';
import { NudgeToast } from './components/NudgeToast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { trackCaptureEvent } from './lib/captureAnalytics';
// Re-exported so existing importers (and tests) keep resolving it from here;
// the implementation lives in lib/ so errorReporting can use it without
// closing an app → ErrorBoundary → errorReporting cycle.
import { routePattern } from './lib/routePattern';

export { routePattern };

const RecapPage = lazy(() =>
  import('./pages/RecapPage').then((m) => ({ default: m.RecapPage })),
);
const PhaseRecapPage = lazy(() =>
  import('./pages/PhaseRecapPage').then((m) => ({ default: m.PhaseRecapPage })),
);
const SubscriptionPage = lazy(() =>
  import('./pages/SubscriptionPage').then((m) => ({ default: m.SubscriptionPage })),
);
const SubscriptionSuccessPage = lazy(() =>
  import('./pages/SubscriptionSuccessPage').then((m) => ({ default: m.SubscriptionSuccessPage })),
);
const SubscriptionCancelPage = lazy(() =>
  import('./pages/SubscriptionCancelPage').then((m) => ({ default: m.SubscriptionCancelPage })),
);
const SupportPage = lazy(() =>
  import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })),
);
const TermsPage = lazy(() =>
  import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const HowItWorksPage = lazy(() =>
  import('./pages/HowItWorksPage').then((m) => ({ default: m.HowItWorksPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const CancelDeletionPage = lazy(() =>
  import('./pages/CancelDeletionPage').then((m) => ({ default: m.CancelDeletionPage })),
);

/**
 * Sampled so page views don't dominate ingest — each event costs ~8-10 DB
 * round-trips, and route changes are the highest-frequency thing a session
 * does. Error events are never sampled.
 */
const PAGE_VIEW_SAMPLE_RATE = 0.5;

function handleRouteChange(event: { url?: string }): void {
  if (Math.random() >= PAGE_VIEW_SAMPLE_RATE) return;
  // `url` carries the raw path including any invite token, so report the
  // matched route pattern instead of the concrete URL.
  const path = (event.url ?? '/').split('?')[0];
  trackCaptureEvent('page_view', { surface: 'app', route: routePattern(path) });
}

function RouteFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <Spinner size={32} />
    </div>
  );
}

export function App() {
  const { guestSession, accessToken } = useAuth();

  // Open the app-wide socket. The socket resolves its own credentials from
  // localStorage on every (re)connection attempt (guest-first, JWT refresh —
  // see lib/socket.ts); this key only gates connecting and forces a fresh
  // handshake when the on-device identity changes mid-session.
  const socketIdentity =
    guestSession?.sessionToken ?? (accessToken ? 'jwt' : null);
  useSocket(socketIdentity);

  return (
    <Suspense fallback={<RouteFallback />}>
      <UpdateAvailableBanner />
      <NudgeToast />
      <ErrorBoundary>
      <Router onChange={handleRouteChange}>
        <JoinPage path="/join/:token" />
        <JoinPage path="/phase-invite/:token" isPhaseInvite />
        <CodeJoinPage path="/join-code/:code" />
        <ContributePage path="/contribute/:threadId" />
        <ThreadPage path="/thread/:threadId" />
        <RecapPage path="/r/:shareToken" />
        <RecapPage path="/recap/:shareToken" />
        <PhaseRecapPage path="/spaces/:spaceId/threads/:threadId/phase-recap/:phaseIndex" />
        <SubscriptionPage path="/subscription" />
        <SubscriptionSuccessPage path="/subscription/success" />
        <SubscriptionCancelPage path="/subscription/cancel" />
        <SupportPage path="/support" />
        <TermsPage path="/terms" />
        <PrivacyPage path="/privacy" />
        <HowItWorksPage path="/how-it-works" />
        <ResetPasswordPage path="/reset-password/:token" />
        <CancelDeletionPage path="/cancel-deletion/:token" />
        <HomePage path="/" />
        <NotFound default />
      </Router>
      </ErrorBoundary>
    </Suspense>
  );
}
